import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Rocket } from 'lucide-react';
import SICoachChat from '../components/SICoachChat';

const GRAD = 'linear-gradient(135deg, #06b6d4, #4f46e5)';

const SUGGESTIONS = [
  { icon: '🌅', label: 'My OPD in 2030', q: 'Paint my typical OPD morning in 2030 — walk me through it' },
  { icon: '🤖', label: 'AI in My Practice', q: 'What will AI actually do in a gynaecology practice by 2030?' },
  { icon: '🔬', label: 'Surgery in 2030', q: 'How will gynaec surgery change by 2030 — robotics, imaging, recovery?' },
  { icon: '📱', label: 'Telehealth & Remote Care', q: 'How will remote monitoring and telehealth reshape antenatal care by 2030?' },
  { icon: '🧬', label: 'Genomics & Precision Care', q: 'What role will genomics play in everyday gynaecology by 2030?' },
  { icon: '👩', label: 'The Patient of 2030', q: 'How will patient expectations change by 2030 and how should I prepare?' },
  { icon: '🛡️', label: 'Future-Proof My Practice', q: 'Give me a 12-month roadmap to future-proof my practice starting now' },
  { icon: '💡', label: 'New Revenue Streams', q: 'What new practice and revenue models will exist for gynaecologists by 2030?' },
];

const MD_COMPONENTS = {
  h2: (props) => <h2 className="text-lg font-bold text-cyan-200 mt-4 mb-2" {...props} />,
  h3: (props) => <h3 className="text-base font-bold text-cyan-200 mt-3 mb-1.5" {...props} />,
  p: (props) => <p className="text-white/85 mb-3" {...props} />,
  strong: (props) => <strong className="text-white font-bold" {...props} />,
  ul: (props) => <ul className="list-disc pl-5 mb-3 space-y-1.5 text-white/85 marker:text-cyan-300" {...props} />,
  ol: (props) => <ol className="list-decimal pl-5 mb-3 space-y-1.5 text-white/85 marker:text-cyan-300" {...props} />,
};

export default function Gynovate2030Page() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen text-white" style={{ background: 'radial-gradient(1200px 700px at 20% -10%, rgba(6,182,212,0.15), transparent), radial-gradient(900px 500px at 95% 10%, rgba(79,70,229,0.18), transparent), #0a0d1a' }}>
      <header className="sticky top-0 z-20 backdrop-blur-xl border-b border-white/10" style={{ background: 'rgba(10,13,26,0.8)' }}>
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/chat')} data-testid="g2030-back-btn" className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white whitespace-nowrap">
            <ArrowLeft className="w-4 h-4" /> Copilot
          </button>
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center text-sm" style={{ background: GRAD }}>🚀</span>
            <span className="text-xs font-black tracking-[0.2em] uppercase">Gynovate 2030</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 pb-16 pt-10">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-cyan-300/70 mb-3 flex items-center gap-2"><Rocket className="w-3.5 h-3.5" /> The Future of Gynaecology Practice</p>
        <h1 className="text-4xl sm:text-5xl font-black leading-[1.05] tracking-tight mb-3">
          Practice like it's<br />
          <span style={{ background: GRAD, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>already 2030.</span>
        </h1>
        <p className="text-white/60 text-sm sm:text-base mb-8 max-w-xl">An imaginative space for what gynaecologists will be doing in 2030 — how practice will look, what's already here, and the concrete steps to future-proof yours today.</p>

        <SICoachChat
          coach="future2030"
          source="Gynovate 2030"
          accentGrad={GRAD}
          accentText="text-cyan-300/70"
          chipClass="border-white/10 bg-white/[0.04] hover:border-cyan-300/50 hover:bg-cyan-400/10"
          suggestions={SUGGESTIONS}
          placeholder="Ask anything about practice in 2030…"
          mdComponents={MD_COMPONENTS}
          planExport={{
            title: 'My Future-Proof Plan — Gynovate 2030',
            buttonLabel: 'Download My Future-Proof Plan (PDF)',
            footer: 'Gynovate 2030 — imaginative scenarios & future-proofing guidance, not predictions. © Gynovate AI/SI',
          }}
        />
      </main>
    </div>
  );
}
